import type { TStrSevCol } from '@ipfabric/api-client/client/schemas';
import { Card } from '@ipfabric/ui-components/Card/Card';
import type { IconGlyph } from '@ipfabric/ui-components/Icon/IconGlyph';
import { Link } from '@ipfabric/ui-components/Link/Link';
import { SidePanel } from '@ipfabric/ui-components/SidePanel/SidePanel';
import {
  type KeyOfType,
  type ReportSeverity,
  getValueFromKeyOfType,
} from '@ipfabric/utils';
import type { TableCellContentRenderFunction } from 'components/Table/model/TableCellContentRenderFunction';
import { Fragment, useState } from 'react';

type CreateRenderPlaintextModalCellArgs<TRow> = {
  cellButtonIcon?: IconGlyph;
  cellButtonTitle: string;
  // String columns in tables are optional keys of TStrSevCol type
  key: KeyOfType<TRow, TStrSevCol | undefined>;
  modalTitle: string | ((row: TRow) => string);
  textAreaTitle: string | ((row: TRow) => string);
};

type PlaintextModalCellProps = {
  cellButtonIcon?: IconGlyph;
  cellButtonTitle: string;
  content: string | null;
  modalTitle: string;
  severity: ReportSeverity | null;
  textAreaTitle: string;
};

const PlaintextModalCell = (props: PlaintextModalCellProps) => {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <>
      <Link
        renderAs='button'
        icon={props.cellButtonIcon}
        iconPosition='start'
        onClick={() => {
          setModalOpen(true);
        }}
        accent={props.severity === null ? 'positive' : 'contrast'}
      >
        {props.cellButtonTitle}
      </Link>
      {modalOpen && (
        <SidePanel
          size='large'
          closeOnBackdropClick
          title={props.modalTitle}
          onClose={() => {
            setModalOpen(false);
          }}
        >
          <Card className='m'>
            <div className='px py-sm border-b border-solid border-divider font-heading text-level-5 font-semibold'>
              {props.textAreaTitle}
            </div>
            <div className='p pt-sm'>
              {props.content === null ? (
                <em className='text-status-disabled'>Empty</em>
              ) : (
                <div className='grid grid-cols-[max-content,1fr] gap-x items-start font-mono'>
                  {props.content.split('\n').map((row, index) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: no better key there
                    <Fragment key={index}>
                      <div className='text-right text-shade-40 whitespace-pre'>
                        {index + 1}
                      </div>
                      <div className='whitespace-pre-wrap wrap-anywhere'>
                        {row}
                      </div>
                    </Fragment>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </SidePanel>
      )}
    </>
  );
};

export const createRenderPlaintextModalCell =
  <TRow,>(
    args: CreateRenderPlaintextModalCellArgs<TRow>,
  ): TableCellContentRenderFunction<TRow> =>
  ({ row, severity }) => {
    return {
      content: (
        <PlaintextModalCell
          cellButtonIcon={args.cellButtonIcon}
          cellButtonTitle={args.cellButtonTitle}
          content={getValueFromKeyOfType(row, args.key) ?? null}
          modalTitle={
            typeof args.modalTitle === 'string'
              ? args.modalTitle
              : args.modalTitle(row)
          }
          textAreaTitle={
            typeof args.textAreaTitle === 'string'
              ? args.textAreaTitle
              : args.textAreaTitle(row)
          }
          severity={severity}
        />
      ),
    };
  };
